import { ApolloProvider } from '@apollo/client';
import { GenericError, useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo } from 'react';

import { useLogOut } from '../hooks/useLogOut';
import { createApolloClient } from '../services/apollo';

interface Props {
  children: React.ReactNode;
}

function AppApolloProvider({ children }: Props): JSX.Element {
  const { getAccessTokenSilently } = useAuth0();
  const logOut = useLogOut();

  // Auth0 keeps reporting the user as authenticated (stale cached user) even
  // after its refresh token is rejected, so we have to force a logout here
  // ourselves once we see that specific error.
  const getAccessToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      if (error instanceof GenericError && error.error === 'invalid_grant') {
        logOut();
        return undefined;
      }
      throw error;
    }
  }, [getAccessTokenSilently, logOut]);

  const client = useMemo(
    () => createApolloClient({ getAccessToken }),
    [getAccessToken],
  );

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}

export default AppApolloProvider;
